/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import axios from 'axios'
import { HttpRequest } from '@smithy/protocol-http'
import { SignatureV4 } from '@smithy/signature-v4'
import { Sha256 } from '@aws-crypto/sha256-js'
import { fetchAuthSession } from 'aws-amplify/auth'

export async function AxiosInterceptor(): Promise<void> {
  axios.interceptors.request.use(async (config) => {
    try {
      const session = await fetchAuthSession()

      if (!session.credentials) {
        return config
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const url = new URL(config.url!, config.baseURL)

      // Stringify body ONCE and ensure axios sends exactly what we signed
      let body: string | undefined
      if (config.data) {
        body =
          typeof config.data === 'string'
            ? config.data
            : JSON.stringify(config.data)
        // Set it back to config so axios sends the exact same string
        config.data = body
      }

      // Prepare headers for signing
      const headers: Record<string, string> = {
        host: url.hostname,
      }

      // Add content-type only if there's a body
      if (body) {
        headers['content-type'] = 'application/json'
      }

      // Add other headers from config
      Object.keys(config.headers || {}).forEach((key) => {
        const lowerKey = key.toLowerCase()
        if (lowerKey !== 'host' && lowerKey !== 'content-length') {
          headers[key] = config.headers?.[key]
        }
      })

      // Create HTTP request for signing
      const httpRequest = new HttpRequest({
        method: config.method?.toUpperCase() || 'GET',
        protocol: url.protocol,
        hostname: url.hostname,
        path: url.pathname,
        query: Object.fromEntries(url.searchParams.entries()),
        headers,
        body, // Pass the stringified body
      })

      // Sign the request
      const signer = new SignatureV4({
        credentials: {
          accessKeyId: session.credentials.accessKeyId,
          secretAccessKey: session.credentials.secretAccessKey,
          sessionToken: session.credentials.sessionToken,
        },
        region: 'eu-central-1',
        service: 'execute-api',
        sha256: Sha256,
        applyChecksum: false, // Don't add content-sha256 header
      })

      const signedRequest = await signer.sign(httpRequest)

      // Apply only browser-safe headers
      Object.keys(signedRequest.headers).forEach((key) => {
        const lowerKey = key.toLowerCase()
        if (lowerKey !== 'host' && lowerKey !== 'content-length') {
          config.headers.set(key, signedRequest.headers[key])
        }
      })

      // Add custom token headers
      const accessToken = session.tokens?.accessToken?.toString()
      const idToken = session.tokens?.idToken?.toString()

      if (accessToken) {
        config.headers['X-Amz-Access-Token'] = accessToken
      }
      if (idToken) {
        config.headers['X-Amz-Id-Token'] = idToken
      }

      return config
    } catch (err) {
      return config
    }
  })
}
